<template>
  <div>
    <!-- PopUp of confirmation deleted:start -->
    <AlertMessage ref="alertMessage" id_alert="deleted-confirmation-alert" class="dark" />
    <!-- PopUp of confirmation deleted:end -->

    <!-- Messages-Confirmations:start -->
    <ConfirmMessages :ids="messages" />
    <!-- Messages-Confirmations:end -->

    <!-- Title-and-Name:start -->
    <div class="intro-y mt-8">
      <h2 class="text-lg font-medium mr-auto">
        {{ !alerts.id ? $t(`${viewRoot}.new`) : $t(`${viewRoot}.edit`) + ": " + alerts.id }}
      </h2>
    </div>
    <!-- Title-and-Name:end -->

    <!-- Loader:start-->
    <div v-if="initialLoading" class="intro-y w-full h-full flex justify-center items-center">
      <div class="wrapper__loading">
        <LoadingIcon icon="tail-spin" />
      </div>
    </div>
    <!-- Loader:end-->

    <!-- Main content:start -->
    <div v-else class="flex flex-row mt-5 mb-12 w-full gap-4">
      <div class="w-full">
        <!-- Tabs-Languages:start -->
        <div class="lang__tabs nav nav-tabs intro-y flex-col sm:flex-row text-gray-600 rounded-b-none" role="tablist">
          <a
            v-for="(lang, langKey) in langs"
            :key="langKey"
            data-toggle="tab"
            @click="changeLang('lang__' + lang.key)"
            :data-target="'#lang__' + lang.key"
            class="tooltip w-full sm:w-40 py-4 text-center flex justify-center items-center rounded-t-md"
            :class="activeLang === 'lang__' + lang.key ? 'active' : ''"
            :id="'lang__' + lang.key + '-tab'"
            role="tab"
            :aria-controls="'lang__' + lang.key"
            aria-selected="false"
          >
            {{ $t("components.langs." + lang.key + ".name") }}
          </a>
        </div>
        <!-- Tabs-Languages:end -->

        <!-- Form:start -->
        <div class="intro-y col-span-12 lg:col-span-6">
          <AlertsModule
            v-model="alerts"
            :langs="langs"
            :active-lang-key="activeLangKey"
            :tagsList="tagsList"
            :nutritional-deficits="nutritionalDeficits"
            :digestive-disorders="digestiveDisorders"
            :read-only="false"
          />
        </div>
        <!-- Form:end -->

        <!-- Buttons:start -->
        <div class="w-full flex justify-end mt-5">
          <button
            v-if="alerts.id"
            @click="confirmRemoveAlert"
            type="button"
            class="btn btn-outline-dark w-24 mr-5"
            :class="{ no__action__btn: noActionBtn, ignore__opacity: removeLoading }"
          >
            <LoadingIcon v-if="removeLoading" icon="three-dots" />
            <span v-else>{{ $t("views.delete") }}</span>
          </button>

          <button
            @click="saveAlert"
            type="button"
            class="btn btn-primary w-24"
            :class="{ no__action__btn: noActionBtn, ignore__opacity: saveLoading }"
          >
            <LoadingIcon v-if="saveLoading" icon="three-dots" />
            <span v-else>{{ $t("views.save") }}</span>
          </button>
        </div>
        <!-- Buttons:end -->
      </div>
    </div>
    <!-- Main content:end -->
  </div>
</template>

<script>
import Toastify from "toastify-js";

import AlertMessage from "@/components/alert/Main.vue";
import ConfirmMessages from "@/components/message/ConfirmMessages.vue";
import AlertsModule from "@/views/alerts/Module.vue";

import AlertsService from "@/services/alerts.service";
import DeficitNutritionalService from "@/services/nutritional-deficit.service";
import DigestiveDisordersService from "@/services/digestive-disorder.service";
import TagService from "@/services/tag.service";

import { ensureShape, validateAlert, formatToSend, parseFromApi } from "@/utils/modules/alerts";

export default {
  name: "AlertsNew",
  components: {
    ConfirmMessages,
    AlertMessage,
    AlertsModule,
  },
  data() {
    return {
      // Data (seed example; ensureShape normalizes de todos modos)
      alerts: {
        id: null,
        mini_guide_id: null,
        langs: {
          es: { title: "", content: "" },
          en: { title: "", content: "" },
        },
        biological_sex: "BOTH",
        action_type: "NONE",
        hormonal_imbalances: [],
        nutritionalDeficits: [],
        time_of_day: "BEDTIME",
        age_ranges: [],
        tags: [],
        status: "PENDING_APPROVAL",
        validations: {
          title: "",
          content: "",
          tags: "",
          age_ranges: "",
        },
      },

      // Lists
      nutritionalDeficits: [],
      digestiveDisorders: [],
      tagsList: [],

      // Common
      viewRoot: "views.alerts",
      queryUrl: this.$route.query,

      // UI
      noActionBtn: false,
      saveLoading: false,
      removeLoading: false,
      initialLoading: true,
      messages: {
        savedSuccessId: "saved-confirmation-success",
        updatedSuccessId: "updated-confirmation-success",
        removedSuccessId: "removed-confirmation-success",
      },

      // Langs
      langs: this.$global.getLangs(),
      lang_key: localStorage.lang ? localStorage.lang : navigator.language.split("-")[0],
      activeLangKey: localStorage.lang ? localStorage.lang : navigator.language.split("-")[0],
      activeLang: "lang__" + (localStorage.lang ? localStorage.lang : navigator.language.split("-")[0]),
    };
  },
  created() {
    this.generateInitialStructure();
    this.getInitialData();
  },
  methods: {
    /**
     * * Initial Structure
     */
    generateInitialStructure() {
      this.alerts = ensureShape(this.alerts, this.langs);
    },

    /**
     * * Initial Data (edit mode)
     */
    async getInitialData() {
      await Promise.all([
        this.getAlertById(),
        this.loadNutritionalDeficits(),
        this.loadDigestiveDisorders(),
        this.loadTags(),
      ]);
      this.initialLoading = false;
    },
    async getAlertById() {
      if (!this.queryUrl.id) return;
      try {
        const { data } = await AlertsService.getById(this.queryUrl.id);
        if (!data) return;
        this.alerts = parseFromApi(data, this.langs);
      } catch (_) {
        console.log("CODE#404");
      }
    },

    /**
     * * LOADS DATA
     */
    async loadNutritionalDeficits() {
      try {
        const res = await DeficitNutritionalService.getNutritionalDeficits({}, localStorage.lang);
        this.nutritionalDeficits = Array.isArray(res) ? res : res?.data || [];
      } catch {
        this.nutritionalDeficits = [];
      }
    },
    async loadDigestiveDisorders() {
      try {
        const res = await DigestiveDisordersService.getDigestiveDisorders({}, localStorage.lang);
        this.digestiveDisorders = Array.isArray(res) ? res : res?.data || [];
      } catch {
        this.digestiveDisorders = [];
      }
    },
    async loadTags() {
      try {
        const res = await TagService.getAllList(localStorage.lang);

        if (!res.success) {
          this.tagsList = [];
          return;
        }

        const formattedTag = (tag) => ({ id: tag.tag_id, name: tag.name });

        this.tagsList = (res.data?.list || []).map(formattedTag);
      } catch {
        this.tagsList = [];
      }
    },

    /**
     * * Methods Save & Remove
     */
    saveAlert() {
      this.saveLoading = true;
      this.noActionBtn = true;

      const isValid = validateAlert(this.alerts, this.langs, {
        requireTags: true,
        requireAgeRanges: true,
        t: this.$t,
      });

      if (!isValid) {
        this.saveLoading = false;
        this.noActionBtn = false;
        return;
      }

      const [payload, editMode] = formatToSend(this.alerts);
      const finallyBlock = () => {
        this.saveLoading = false;
        this.noActionBtn = false;
      };

      const req = editMode ? AlertsService.update(payload.id, payload) : AlertsService.create(payload);

      req
        .then(() => {
          this.showToast(editMode ? this.messages.updatedSuccessId : this.messages.savedSuccessId);
          this.goToList();
        })
        .catch(() => {
          console.info("contact support:", editMode ? "#ALERT-002" : "#ALERT-001");
        })
        .finally(finallyBlock);
    },

    confirmRemoveAlert() {
      this.removeLoading = true;
      this.noActionBtn = true;

      const styleInline = 'style="text-wrap: pretty;"';
      const message = `
        <h2 class="text-2xl mb-5 font-bold" ${styleInline}>${this.$t(`${this.viewRoot}.message_delete_confirm_1`)}</h2>
        <p ${styleInline}>${this.$t(`${this.viewRoot}.message_delete_confirm_2`)}</p>
      `;
      this.$refs.alertMessage.show(
        message,
        true,
        true,
        this.removeAlert,
        true,
        null,
        null,
        null,
        null,
        null,
        null,
        null,
        this.cancelWarning
      );
    },

    cancelWarning() {
      this.removeLoading = false;
      this.noActionBtn = false;
    },

    removeAlert() {
      if (!this.alerts.id) {
        this.$refs.alertMessage.hide(); // use cancel warning
        return;
      }

      AlertsService.delete(this.alerts.id)
        .then(() => {
          this.showToast(this.messages.removedSuccessId);
          this.$refs.alertMessage.hide();
          this.goToList();
        })
        .catch(() => {
          console.info("contact support: #ALERT-003");
        })
        .finally(this.cancelWarning);
    },

    goToList() {
      this.$router.push({ path: "/alerts-list" });
    },

    /**
     * Show Toast
     */
    showToast(id) {
      Toastify({
        node: cash(`#${id}`).clone().removeClass("hidden")[0],
        duration: 3000,
        gravity: "top",
        position: "right",
        close: true,
      }).showToast();
    },

    /**
     * * LANGS CONFIGs
     */
    changeLang(l) {
      this.activeLang = l;
      const k = l.replace("lang__", "");
      this.activeLangKey = k;
      this.lang_key = k;
    },
    getLang(lang) {
      return this.$t(`components.langs.${lang}.name`);
    },
  },
};
</script>

<style lang="scss" scoped>
.no__action__btn {
  pointer-events: none;
  opacity: 0.5;

  &.ignore__opacity {
    opacity: 1;
  }
}

.wrapper__loading {
  width: 20%;
  transform: translateY(100%);
}
</style>
